import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Drawer,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { suppliers } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtDate } from "../utils/format";

export default function Suppliers() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [q, setQ] = useState("");
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const r = await suppliers.list();
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModal(true);
  };

  // FIX: Open modal first, then set form values so the fields exist in DOM
  const openEdit = (rec) => {
    setEditing(rec);
    setModal(true);
    // setTimeout ensures the modal is mounted before we populate fields
    setTimeout(() => {
      form.setFieldsValue({
        name: rec.name,
        phone: rec.phone,
        address: rec.address,
      });
    }, 0);
  };

  const openView = async (rec) => {
    // Use the already-loaded record directly; no extra API call needed
    setViewing(rec);
  };

  const onSave = async (vals) => {
    try {
      if (editing?.id) {
        await suppliers.update(editing.id, vals);
        message.success("Supplier updated");
      } else {
        await suppliers.create(vals);
        message.success("Supplier created");
      }
      setModal(false);
      setEditing(null);
      form.resetFields();
      load();
    } catch {
      message.error("Failed to save supplier");
    }
  };

  const onDelete = async (id) => {
    try {
      await suppliers.delete(id);
      message.success("Supplier deleted");
      load();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Cannot delete — supplier may be linked to purchases";
      message.error(msg);
    }
  };

  const cols = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v) => (
        <Space>
          <ShopOutlined style={{ color: "var(--accent)" }} />
          <span
            className="mono"
            style={{ color: "var(--text)", fontWeight: 600 }}
          >
            {v}
          </span>
        </Space>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (v) =>
        v ? (
          <span className="mono">{v}</span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>—</span>
        ),
    },
    {
      title: "Address",
      dataIndex: "address",
      render: (v) =>
        v ? (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{v}</span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>—</span>
        ),
    },
    {
      title: "Added On",
      dataIndex: "createdAt",
      render: (v) => (
        <span
          style={{
            fontSize: 12,
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {fmtDate(v)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, rec) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openView(rec)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(rec)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this supplier?"
            description="This cannot be undone."
            onConfirm={() => onDelete(rec.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Suppliers"
        subtitle="Manage your product suppliers"
        actions={[
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            Add Supplier
          </Button>,
        ]}
      />

      {/* ── Filter ───────────────────────────────── */}
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search name, phone, address..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        allowClear
        style={{ width: 280, marginBottom: 16 }}
      />

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={
          q
            ? data.filter((s) =>
                [s.name, s.phone, s.address]
                  .filter(Boolean)
                  .some((f) => f.toLowerCase().includes(q.toLowerCase()))
              )
            : data
        }
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        summary={(pageData) => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text-muted)",
                }}
              >
                {q ? `${pageData.length} of ${data.length} suppliers` : `Total: ${data.length} suppliers`}
              </span>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      {/* ── View Drawer ── */}
      <Drawer
        open={!!viewing}
        title="Supplier Details"
        onClose={() => setViewing(null)}
        width={400}
        extra={
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setViewing(null);
              openEdit(viewing);
            }}
          >
            Edit
          </Button>
        }
      >
        {viewing && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Name">{viewing.name}</Descriptions.Item>
            <Descriptions.Item label="Phone">
              {viewing.phone || (
                <span style={{ color: "var(--text-muted)" }}>—</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {viewing.address || (
                <span style={{ color: "var(--text-muted)" }}>—</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Added On">
              {fmtDate(viewing.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={modal}
        title={editing?.id ? "Edit Supplier" : "New Supplier"}
        onCancel={() => {
          setModal(false);
          setEditing(null);
          form.resetFields();
        }}
        footer={null}
        // FIX: removed destroyOnClose — it was unmounting form fields before
        // setFieldsValue could populate them, causing edit to show empty fields
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSave}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="name"
            label="Supplier Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. ABC Traders" />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="e.g. 9876543210" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={3} placeholder="Full address..." />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setModal(false);
                setEditing(null);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editing?.id ? "Update Supplier" : "Create Supplier"}
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}